import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CodeMirror from "@uiw/react-codemirror";
import { sql } from "@codemirror/lang-sql";
import { dracula } from "@uiw/codemirror-theme-dracula";
import { toast } from "sonner";
import { Play, RotateCcw, Sparkles, Loader2, Clock, Hash, AlertTriangle, Database, History, ListChecks } from "lucide-react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SchemaSidebar from "@/components/sql/SchemaSidebar";
import ResultsGrid from "@/components/sql/ResultsGrid";

const DIFFICULTIES = ["beginner", "intermediate", "advanced"];
const DEFAULT_QUERIES = {
  sfmc_data_views: "SELECT SubscriberKey, EventDate FROM _Open LIMIT 10;",
  admin_dev_soql: "SELECT Name, Status__c, Region__c FROM Shipment__c LIMIT 10;",
  servicenow_glide: "SELECT number, short_description, priority FROM incident LIMIT 10;",
};
const DEFAULT_QUERY_FALLBACK = "SELECT first_name, last_name, country FROM Individual LIMIT 10;";

export default function SqlPlaygroundPage() {
  const [searchParams] = useSearchParams();
  const [datasets, setDatasets] = useState([]);
  const [dataset, setDataset] = useState("retail");
  const [schema, setSchema] = useState(null);
  const [query, setQuery] = useState(DEFAULT_QUERY_FALLBACK);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [difficulty, setDifficulty] = useState("beginner");
  const [practiceQuestions, setPracticeQuestions] = useState([]);
  const [history, setHistory] = useState([]);
  const [sidebarTab, setSidebarTab] = useState("schema");

  useEffect(() => {
    api.get("/sql/datasets").then((r) => setDatasets(r.data));
    api.get("/sql/history").then((r) => setHistory(r.data));
    // Skip the default schema fetch if a dataset was requested via the URL -
    // the requestedDataset effect below will reset & load it exclusively,
    // avoiding a race between the two schema fetches.
    if (!searchParams.get("dataset")) {
      api.get("/sql/schema").then((r) => {
        setSchema(r.data.schema); setDataset(r.data.dataset);
        setQuery(DEFAULT_QUERIES[r.data.dataset] || DEFAULT_QUERY_FALLBACK);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestedDataset = searchParams.get("dataset");
  useEffect(() => {
    if (requestedDataset) {
      changeDataset(requestedDataset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedDataset]);

  useEffect(() => {
    api.get(`/sql/practice-questions?difficulty=${difficulty}&dataset=${dataset}`).then((r) => setPracticeQuestions(r.data));
  }, [difficulty, dataset]);

  const changeDataset = async (id) => {
    if (id === dataset) return;
    setResetting(true);
    try {
      await api.post("/sql/reset", { dataset: id });
      const { data } = await api.get("/sql/schema");
      setSchema(data.schema); setDataset(data.dataset); setResult(null); setOptimizeResult(null);
      setQuery(DEFAULT_QUERIES[data.dataset] || DEFAULT_QUERY_FALLBACK);
      toast.success(`Switched to ${id} dataset`);
    } finally {
      setResetting(false);
    }
  };

  const resetDatabase = async () => {
    setResetting(true);
    try {
      await api.post("/sql/reset", { dataset });
      const { data } = await api.get("/sql/schema");
      setSchema(data.schema); setResult(null); setOptimizeResult(null);
      toast.success("Database reset to fresh sample data");
    } finally {
      setResetting(false);
    }
  };

  const runQuery = async () => {
    setExecuting(true); setOptimizeResult(null);
    try {
      const { data } = await api.post("/sql/execute", { query });
      setResult(data);
      const { data: hist } = await api.get("/sql/history");
      setHistory(hist);
    } catch (e) {
      toast.error("Failed to execute query");
    } finally {
      setExecuting(false);
    }
  };

  const optimizeQuery = async () => {
    setOptimizing(true);
    try {
      const { data } = await api.post("/sql/optimize", { query });
      setOptimizeResult(data);
    } finally {
      setOptimizing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="sql-playground-page">
      <div className="flex items-center gap-3 mb-2">
        <Database className="w-6 h-6 text-indigo-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Hands-on</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">AI SQL Playground</h1>
      <p className="text-sm text-slate-400 mt-2 max-w-2xl">Write and run real SQL against sandboxed Data Cloud sample datasets. AI explains errors and suggests optimizations.</p>

      <div className="flex flex-wrap gap-2 mt-6">
        {datasets.map((d) => (
          <button
            key={d.id} onClick={() => changeDataset(d.id)} disabled={resetting} data-testid={`sql-dataset-${d.id}`}
            className={`text-xs font-semibold px-4 py-2 rounded-full border transition-colors ${dataset === d.id ? "border-indigo-400 bg-indigo-400/10 text-indigo-300" : "border-white/10 text-slate-400 hover:border-white/25"}`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="glass-panel rounded-2xl p-1 overflow-hidden" data-testid="sql-editor">
            <CodeMirror value={query} height="220px" theme={dracula} extensions={[sql()]} onChange={(val) => setQuery(val)} />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={runQuery} disabled={executing} data-testid="sql-run-btn" className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60">
              {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run Query
            </button>
            <button onClick={optimizeQuery} disabled={optimizing} data-testid="sql-optimize-btn" className="flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white text-sm font-semibold px-4 py-2.5 rounded-lg disabled:opacity-60">
              {optimizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI Optimize
            </button>
            <button onClick={resetDatabase} disabled={resetting} data-testid="sql-reset-db-btn" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm font-semibold px-3 py-2.5 rounded-lg disabled:opacity-60 ml-auto">
              <RotateCcw className="w-4 h-4" /> Reset Database
            </button>
          </div>

          {result && (
            <div className="glass-panel rounded-2xl p-5" data-testid="sql-result-panel">
              {result.success ? (
                <>
                  <div className="flex items-center gap-4 text-xs text-slate-400 mb-3">
                    <span className="flex items-center gap-1"><Hash className="w-3.5 h-3.5" /> {result.row_count} rows</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {result.execution_time_ms} ms</span>
                  </div>
                  <ResultsGrid columns={result.columns} rows={result.rows} />
                </>
              ) : (
                <div data-testid="sql-error-panel">
                  <p className="flex items-center gap-2 text-sm font-semibold text-red-400 mb-2"><AlertTriangle className="w-4 h-4" /> {result.error.message}</p>
                  <div className="rounded-lg border border-indigo-400/20 bg-indigo-400/5 p-3">
                    <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-indigo-400 mb-1"><Sparkles className="w-3 h-3" /> AI Explanation</span>
                    <p className="text-sm text-slate-300" data-testid="sql-ai-error-explanation">{result.error.ai_explanation}</p>
                    {result.error.suggested_fix && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-[11px] text-slate-400 mb-1">Suggested fix:</p>
                        <code className="text-xs text-emerald-300 block bg-black/20 rounded p-2 mb-2" data-testid="sql-suggested-fix">{result.error.suggested_fix}</code>
                        <button onClick={() => setQuery(result.error.suggested_fix)} data-testid="sql-apply-fix-btn" className="text-xs font-semibold text-indigo-400 hover:text-indigo-300">Apply Fix to Editor</button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {optimizeResult && (
            <div className="glass-panel rounded-2xl p-5" data-testid="sql-optimize-panel">
              <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-400 mb-2">AI Optimization Suggestions</p>
              <ul className="text-sm text-slate-300 space-y-1 mb-3 list-disc pl-4">
                {optimizeResult.suggestions.map((s, i) => <li key={`${i}-${s}`}>{s}</li>)}
              </ul>
              <code className="text-xs text-emerald-300 block bg-black/20 rounded p-2 mb-3">{optimizeResult.optimized_query}</code>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Best Practices</p>
              <ul className="text-xs text-slate-400 space-y-1 list-disc pl-4">
                {optimizeResult.best_practices.map((b, i) => <li key={`${i}-${b}`}>{b}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="glass-panel rounded-2xl p-4">
          <Tabs value={sidebarTab} onValueChange={setSidebarTab}>
            <TabsList data-testid="sql-sidebar-tabs" className="w-full grid grid-cols-3">
              <TabsTrigger value="schema" data-testid="sql-sidebar-tab-schema"><Database className="w-3.5 h-3.5" /></TabsTrigger>
              <TabsTrigger value="practice" data-testid="sql-sidebar-tab-practice"><ListChecks className="w-3.5 h-3.5" /></TabsTrigger>
              <TabsTrigger value="history" data-testid="sql-sidebar-tab-history"><History className="w-3.5 h-3.5" /></TabsTrigger>
            </TabsList>
            <TabsContent value="schema" className="mt-3"><SchemaSidebar schema={schema} /></TabsContent>
            <TabsContent value="practice" className="mt-3">
              <div className="flex gap-1 mb-3">
                {DIFFICULTIES.map((d) => (
                  <button key={d} onClick={() => setDifficulty(d)} data-testid={`sql-difficulty-${d}`} className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full border ${difficulty === d ? "border-indigo-400 text-indigo-300" : "border-white/10 text-slate-400"}`}>{d}</button>
                ))}
              </div>
              <div className="space-y-2">
                {practiceQuestions.map((q) => (
                  <div key={q.id} className="text-xs bg-white/[0.02] rounded-lg p-2.5" data-testid={`sql-practice-question-${q.id}`}>
                    <p className="text-slate-300">{q.question}</p>
                    <p className="text-slate-400 mt-1 italic">Hint: {q.hint}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="history" className="mt-3">
              <div className="space-y-1.5 max-h-80 overflow-auto">
                {history.length === 0 && <p className="text-xs text-slate-400">No queries run yet.</p>}
                {history.map((h) => (
                  <button key={h.id} onClick={() => setQuery(h.query)} data-testid={`sql-history-item-${h.id}`} className={`w-full text-left text-xs px-2.5 py-2 rounded-lg block truncate ${h.success ? "text-slate-400 hover:bg-white/5" : "text-red-400/70 hover:bg-red-400/5"}`}>
                    {h.query}
                  </button>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
